import { isFocusVisible } from '@react-aria/interactions';
import clsx from 'clsx';
import { AnimatePresence, type HTMLMotionProps, motion, useReducedMotion } from 'motion/react';
import {
    type ComponentPropsWithoutRef,
    type CSSProperties,
    createContext,
    type ForwardedRef,
    forwardRef,
    type ReactNode,
    useCallback,
    useContext,
    useEffect,
    useRef,
    useState,
} from 'react';
import { type ContextValue, HeadingContext, useContextProps } from 'react-aria-components';

import { Icon } from '@gitbook/spine-icons';
import { useEventCallback } from '@gitbook/util-react-hooks';

import { AnimatedStateContext } from '../animations';
import { Backdrop } from '../Backdrop';
import { AtomButton } from '../Button';
import { Overlay, OverlayRoot } from '../Overlay';
import { getDefaultPortalContainer, Portal, PortalContext } from '../Portal';
import { Tooltip, TooltipContext } from '../Tooltip';
import { useCheckIsPopoverOpen } from '../util';
import { SidebarButton } from './SidebarItem';

export interface SidebarProps extends HTMLMotionProps<'aside'> {
    contained?: boolean;
}

export const SidebarContext = createContext<ContextValue<SidebarProps, HTMLDivElement>>({});

const SIDEBAR_MAX_WIDTH = 320;
export const SIDEBAR_INITIAL_WIDTH = 256;
const SIDEBAR_MIN_WIDTH = 200;

/**
 * Sidebar root component.
 */
export const Sidebar = forwardRef(function Sidebar(
    props: SidebarProps,
    ref: ForwardedRef<HTMLDivElement>
) {
    [props, ref] = useContextProps(props, ref, SidebarContext);
    const { contained, ...rest } = props;
    return (
        <HeadingContext.Provider value={{}}>
            <motion.aside
                ref={ref}
                {...props}
                className={clsx(
                    'z-20 flex w-full max-w-[var(--sidebar-max-width,16rem)] select-none flex-col justify-between gap-3 overflow-hidden bg-side py-3',
                    contained ? 'rounded-lg border border-control' : null,
                    rest.className
                )}
            />
        </HeadingContext.Provider>
    );
});

export function ResizableSidebar(props: {
    children: ReactNode;
    isCollapsed: boolean;
    onCollapseChange: (collapsed: boolean) => void;
    width: number | null;
    onWidthChange: (width: number) => void;
    expandMode: 'manual' | 'auto';
}) {
    const {
        isCollapsed,
        onCollapseChange: setCollapse,
        onWidthChange: setWidth,
        children,
        expandMode = 'manual',
    } = props;
    const width = props.width ?? SIDEBAR_INITIAL_WIDTH;
    const [isResizing, setResizing] = useState(false);
    const [isOpen, setOpen] = useState(false);
    /**
     * Lock the opening of the sidebar, after a collapse.
     */
    const isOpenLocked = useRef(false);
    const collapse = useEventCallback(() => {
        if (!isCollapsed) {
            setCollapse(true);
            isOpenLocked.current = true;
        }
    });
    const rafRef = useRef<number | null>(null);
    const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const clear = useEventCallback(() => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }
        if (rafRef.current) {
            cancelAnimationFrame(rafRef.current);
            rafRef.current = null;
        }
    });
    useEffect(() => clear(), [clear]);
    const uncollapse = useEventCallback(() => {
        if (isCollapsed) {
            setCollapse(false);
            isOpenLocked.current = false;

            clear();
            setOpen(false);
        }
    });
    const checkIsPopoverOpen = useCheckIsPopoverOpen({ isDisabled: !isOpen });
    const close = useEventCallback((options?: { delay: number }) => {
        clear();
        rafRef.current = requestAnimationFrame(() => {
            if (checkIsPopoverOpen({ onClose: () => close(options) })) {
                return;
            }
            const delay = options?.delay ?? 150;
            if (delay === 0) {
                setOpen(false);
                return;
            }
            timeoutRef.current = setTimeout(() => {
                setOpen(false);
            }, delay);
        });
    });
    const open = useEventCallback(() => {
        if (isCollapsed && !isOpenLocked.current) {
            clear();
            if (!process.env.GITBOOK_PREDICTABLE_UI) {
                setOpen(true);
            }
        }
    });
    useEffect(() => {
        if (isResizing) {
            document.body.classList.add('resizing-sidebar');

            let moved = false;
            const handleMouseMove = (event) => {
                const x = event.clientX;
                moved = true;
                setWidth(Math.max(Math.min(x, SIDEBAR_MAX_WIDTH), SIDEBAR_MIN_WIDTH));
                if (x < 100) {
                    collapse();
                } else {
                    uncollapse();
                }
            };
            document.addEventListener('mousemove', handleMouseMove);

            const handleMouseUp = () => {
                setResizing(false);
                if (!moved) {
                    collapse();
                }
            };
            document.addEventListener('mouseup', handleMouseUp);
            return () => {
                document.body.classList.remove('resizing-sidebar');
                document.removeEventListener('mousemove', handleMouseMove);
                document.removeEventListener('mouseup', handleMouseUp);
            };
        }
    }, [isResizing, collapse, uncollapse, setWidth]);
    useEffect(() => {
        if (isOpen) {
            const handleMouseOut = (event: MouseEvent) => {
                if (
                    event.clientY <= 0 ||
                    event.clientX <= 0 ||
                    event.clientX >= window.innerWidth ||
                    event.clientY >= window.innerHeight
                ) {
                    close();
                }
            };
            document.addEventListener('mouseout', handleMouseOut);
            return () => {
                document.removeEventListener('mouseout', handleMouseOut);
            };
        }
    }, [isOpen, close]);
    const prefersReducedMotion = useReducedMotion();
    const transition = { duration: prefersReducedMotion ? 0 : 0.18, ease: 'easeInOut' };
    const portalContainerRef = useRef<HTMLDivElement>(null);
    const getPortalContainer = useCallback(
        () => portalContainerRef.current ?? getDefaultPortalContainer(),
        []
    );
    return (
        <PortalContext.Provider value={getPortalContainer}>
            <div
                data-spine-sidebar=""
                className={clsx(
                    'group/resizable-sidebar relative z-20 flex min-h-0 flex-col has-[[data-backdrop]]:z-sidebar'
                )}
                style={
                    {
                        '--sidebar-width': `${width}px`,
                        '--sidebar-max-width': SIDEBAR_MAX_WIDTH,
                    } as CSSProperties
                }
            >
                <motion.div
                    className="z-20 flex min-h-0 w-[--sidebar-width] flex-1"
                    initial={false}
                    transition={transition}
                    style={{ position: 'relative' }}
                    animate={
                        isCollapsed
                            ? isOpen
                                ? {
                                      position: 'absolute',
                                      top: '0.75rem',
                                      bottom: '0.75rem',
                                      translateX: 0,
                                      left: '0.75rem',
                                  }
                                : {
                                      position: 'absolute',
                                      top: '0.75rem',
                                      bottom: '0.75rem',
                                      translateX: '-100%',
                                      left: 0,
                                  }
                            : {
                                  position: 'absolute',
                                  top: 0,
                                  bottom: 0,
                                  translateX: 0,
                                  left: 0,
                                  transitionEnd: {
                                      position: 'relative',
                                  },
                              }
                    }
                    onMouseEnter={() => open()}
                    onMouseLeave={() => close()}
                    onAnimationComplete={() => {
                        isOpenLocked.current = false;
                    }}
                    onFocus={() => {
                        if (isFocusVisible()) {
                            open();
                        }
                    }}
                >
                    <TooltipContext.Provider value={{ disabled: isCollapsed && !isOpen }}>
                        <SidebarContext.Provider value={{ contained: isCollapsed }}>
                            {children}
                        </SidebarContext.Provider>
                    </TooltipContext.Provider>
                    {!isCollapsed && (
                        <Tooltip
                            label="Drag to resize"
                            description="Click to collapse"
                            position="right"
                            disabled={isResizing}
                        >
                            <div
                                onMouseDown={() => {
                                    setResizing(true);
                                }}
                                onMouseUp={() => {
                                    if (!isResizing) {
                                        collapse();
                                    }
                                }}
                                className="group -right-[6px] absolute inset-y-1.5 z-20 flex w-[7px] cursor-col-resize justify-center"
                            >
                                <div className="w-[2px] rounded transition group-hover:bg-[var(--sp-ref-color-teal-400)]" />
                            </div>
                        </Tooltip>
                    )}
                    <SidebarButton
                        data-testid="sidebar.toggleButton"
                        className="absolute right-2 bottom-2 z-20 opacity-0 focus:opacity-100 group-hover/resizable-sidebar:opacity-100"
                        iconOnly
                        outlined
                        label={isCollapsed ? 'Anchor sidebar' : 'Hide sidebar'}
                        icon={isCollapsed ? Icon.PanelLeftOpen : Icon.PanelLeftClose}
                        onPress={() => {
                            if (isCollapsed) {
                                uncollapse();
                            } else {
                                collapse();
                            }
                        }}
                    />
                </motion.div>
                <AnimatePresence>
                    {isOpen && (
                        <motion.div
                            data-backdrop
                            className="fixed inset-0 bg-[rgb(38_41_48_/_0.5)] fill-mode-forwards"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={transition}
                            onClick={() => {
                                close({ delay: 0 });
                            }}
                        />
                    )}
                </AnimatePresence>
                {/* While we animate the sidebar we want to also animate the width of the sidebar. */}
                <motion.div
                    transition={transition}
                    initial={false}
                    animate={isCollapsed ? { width: 0 } : { width: 'var(--sidebar-width)' }}
                />
                {isCollapsed ? (
                    <div
                        className="group absolute inset-y-0 left-0 z-20 w-2"
                        onMouseEnter={() => {
                            if (expandMode === 'auto') {
                                open();
                            }
                        }}
                    >
                        {expandMode === 'manual' ? (
                            <Tooltip label="Expand sidebar" position="right">
                                <AtomButton
                                    className="-translate-x-2 pointer-events-none left-0 h-full w-4 bg-standout-side-active opacity-0 transition group-hover:pointer-events-auto group-hover:translate-x-0 group-hover:cursor-pointer group-hover:opacity-100"
                                    onPress={() => {
                                        uncollapse();
                                    }}
                                />
                            </Tooltip>
                        ) : null}
                    </div>
                ) : null}
                {isOpen && (
                    // biome-ignore lint/a11y/useKeyWithClickEvents: we just need click here
                    <div
                        className="absolute top-0 left-0 z-20 h-3 w-60"
                        onClick={() => uncollapse()}
                        onMouseEnter={() => open()}
                        onMouseLeave={() => close()}
                    />
                )}
                <div ref={portalContainerRef} />
            </div>
        </PortalContext.Provider>
    );
}

export function CollapsibleSidebar(props: {
    children: ReactNode;
    isOpen: boolean;
    onOpenChange: (opened: boolean) => void;
}) {
    const { isOpen, onOpenChange, children } = props;
    return (
        <OverlayRoot isOpen={isOpen} onOpenChange={onOpenChange}>
            <CollapsibleSidebarOverlay>{children}</CollapsibleSidebarOverlay>
        </OverlayRoot>
    );
}

function CollapsibleSidebarOverlay(props: { children: ReactNode }) {
    const { children } = props;
    const animatedState = useContext(AnimatedStateContext);

    const isExited = animatedState === 'exited';
    const isExiting = animatedState === 'exiting';
    const isEntering = animatedState === 'entering';

    return (
        <Portal>
            <Backdrop
                isExiting={isExiting}
                isEntering={isEntering}
                className={isExited ? 'hidden' : undefined}
            />
            <Overlay
                className={clsx(
                    'absolute inset-y-0 left-0 z-20 flex',
                    'fill-mode-forwards [transition-duration:150ms] [transition-timing-function:ease-out]',
                    isExited && 'hidden',
                    isEntering && 'fade-in slide-in-from-left-[10rem] animate-in',
                    isExiting && 'fade-out slide-out-to-left-[10rem] animate-out'
                )}
            >
                {children}
            </Overlay>
        </Portal>
    );
}

export interface SidebarBodyProps extends ComponentPropsWithoutRef<'div'> {}

/**
 * Sidebar body component.
 */
export const SidebarBody = forwardRef(function SidebarBody(
    props: SidebarBodyProps,
    ref: ForwardedRef<HTMLDivElement>
) {
    return (
        <div
            ref={ref}
            {...props}
            className={clsx(
                'scrollbar relative flex w-full flex-1 flex-col items-stretch gap-5 overflow-y-auto overflow-x-hidden px-3 pr-1.5',
                props.className
            )}
        >
            {props.children}
        </div>
    );
});

export interface SidebarFooterProps extends ComponentPropsWithoutRef<'footer'> {}

/**
 * Sidebar footer component.
 */
export const SidebarFooter = forwardRef(function SidebarFooter(
    props: SidebarFooterProps,
    ref: ForwardedRef<HTMLElement>
) {
    return (
        <footer
            ref={ref}
            {...props}
            className={clsx('flex shrink-0 flex-col items-stretch gap-2 px-3', props.className)}
        />
    );
});

export interface SidebarHeaderProps extends ComponentPropsWithoutRef<'header'> {}

/**
 * Sidebar header component.
 */
export const SidebarHeader = forwardRef(function SidebarHeader(
    props: SidebarHeaderProps,
    ref: ForwardedRef<HTMLElement>
) {
    const { children, ...rest } = props;
    return (
        <header
            ref={ref}
            {...rest}
            className={clsx('flex shrink-0 items-center px-3', rest.className)}
        >
            <HeadingContext.Provider
                value={{ level: 1, className: 'truncate text-ui-heading-small' }}
            >
                {children}
            </HeadingContext.Provider>
        </header>
    );
});

export interface SidebarItemListProps extends ComponentPropsWithoutRef<'div'> {}

/**
 * Sidebar item list component.
 */
export const SidebarItemList = forwardRef(function SidebarItemList(
    props: SidebarItemListProps,
    ref: ForwardedRef<HTMLDivElement>
) {
    return <div ref={ref} {...props} className={clsx('flex flex-col gap-px', props.className)} />;
});

export interface SidebarEmptyState extends ComponentPropsWithoutRef<'p'> {}

export const SidebarEmptyState = forwardRef(function SidebarEmptyState(
    props: SidebarEmptyState,
    ref: ForwardedRef<HTMLParagraphElement>
) {
    return (
        <p
            ref={ref}
            {...props}
            className={clsx('mx-1.5 font-normal text-muted text-ui-small', props.className)}
        />
    );
});
